import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';

import Icon from 'components/Icon';
import { useOpportunity } from 'lib/providers/OpportunityProvider';
import { TouchpointType } from 'lib/models/touchpoint';

import styles from './OpportunityApplicationBanner.module.scss';

export const OpportunityApplicationBanner: React.FC = () => {
  const { opportunity, isApplicationClosed, isExpired, isEventEnded, isCapacityReached, isWaitlistEnabled, isApprovalRequired, isAlmostSoldOut } =
    useOpportunity();
  const { touchpointable_type, is_applied } = opportunity;
  const [t] = useTranslation('opportunity');
  const isEvent = touchpointable_type === TouchpointType.Event;

  const { title, message, type } = useMemo(() => {
    let title = '',
      message = '',
      type: 'critical' | 'warning' | 'info' = 'critical';
    if (is_applied) return { title, message, type };

    if (isEventEnded) {
      title = t('event-ended.title');
      message = t('event-ended.message');
    } else if (isApplicationClosed || isExpired) {
      if (isEvent) {
        title = t('event-registration-closed.title');
        message = t('event-registration-closed.message');
      } else {
        title = t('application-closed.title');
        message = t('application-closed.message');
      }
    } else if (isCapacityReached) {
      if (isWaitlistEnabled) {
        title = t('event-sold-out-with-waitlist.title');
        message = t('event-sold-out-with-waitlist.message');
        type = 'warning';
      } else {
        title = t('event-sold-out-no-waitlist.title');
        message = t('event-sold-out-no-waitlist.message');
      }
    } else if (isAlmostSoldOut) {
      title = t('event-almost-sold-out.title');
      message = t('event-almost-sold-out.message');
      type = 'warning';
    } else if (isApprovalRequired) {
      title = t('event-approval-required.title');
      message = t('event-approval-required.message');
      type = 'info';
    }

    return { title, message, type };
  }, [isEvent, isApplicationClosed, isEventEnded, isCapacityReached, isAlmostSoldOut, isWaitlistEnabled, is_applied, t]);

  if (title === '' && message === '') return null;

  return (
    <section className={cx(styles.container, styles[type], { [styles['no-title']]: !title })}>
      <Icon iconName="info" size="xmediumplus" className={styles.icon} />
      <div className={styles.content}>
        {title && <p className={styles.title}>{title}</p>}
        {message && <p className={styles.message}>{message}</p>}
      </div>
    </section>
  );
};

OpportunityApplicationBanner.displayName = 'OpportunityApplicationBanner';
